import React, { useMemo, useState, useEffect } from 'react';
import { fetchAPI } from '../../../services/apiClient';
import { renderTicketPreviewHTML, TicketConfig, TicketConfigApiResponse, TicketPayload } from '../../../services/ticketService';

export const LocalConfigTicketPage: React.FC = () => {
  const [bancaNombre, setBancaNombre] = useState('Tiempos V2S General');
  const [bancaLogo, setBancaLogo] = useState<string | null>(null);
  const [notasTicketDefault, setNotasTicketDefault] = useState('');
  const [leyendaTicket, setLeyendaTicket] = useState('');
  const [tipoImpresora, setTipoImpresora] = useState<'80mm' | '58mm'>('80mm');
  const [telefono, setTelefono] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    cargarConfig();
  }, []);

  const cargarConfig = async () => {
    try {
      const data = await fetchAPI<TicketConfigApiResponse>(
        '/operaciones/local/ticket-config'
      );
      setBancaNombre(data.banca_nombre || 'Tiempos V2S General');
      setBancaLogo(data.banca_logo || null);
      setNotasTicketDefault(data.ticket_notas_default || '');
      setLeyendaTicket(data.leyenda_ticket || '');
      setTipoImpresora(data.tipo_impresora === '58mm' ? '58mm' : '80mm');
      setTelefono(data.telefono || '');
    } catch (error: any) {
      console.error('Error cargando configuración:', error);
    }
  };

  const guardarConfig = async () => {
    setLoading(true);
    setMensaje('');
    try {
      await fetchAPI('/operaciones/local/ticket-config', {
        method: 'PUT',
        body: JSON.stringify({
          ticket_notas_default: notasTicketDefault.trim() || null,
          tipo_impresora: tipoImpresora,
          leyenda_ticket: leyendaTicket.trim() || null,
          telefono: telefono.trim() || null,
        }),
      });
      setMensaje('Configuración guardada correctamente');
      setTimeout(() => setMensaje(''), 3000);
    } catch (error: any) {
      setMensaje(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const previewPayload = useMemo<TicketPayload>(() => {
    const config: TicketConfig = {
      nombre: bancaNombre.trim() || 'Tiempos V2S General',
      notas: notasTicketDefault.trim(),
      ancho: tipoImpresora === '58mm' ? 58 : 80,
      logoUrl: bancaLogo,
      logoDataUrl: null,
      mostrarLogo: Boolean(bancaLogo),
      fontWeight: 'normal',
      fontSize: 'normal',
      leyendaLocal: leyendaTicket.trim() || undefined,
      tipoImpresora,
    };

    return {
      ticketCode: 'TKT-0001',
      ticketId: 'TKT-0001',
      sorteoNombre: 'TICA DIARIA',
      sorteoCierre: '11:30 a. m.',
      sorteoFecha: '25/03/2026',
      fecha: '25/03/2026, 11:06 a. m.',
      vendedorNombre: '',
      localNombre: bancaNombre.trim() || '',
      local: {
        nombre: bancaNombre.trim() || '',
        descripcion: null,
        telefono: telefono.trim() || null,
        logo: bancaLogo,
        esPropio: true,
        grupoId: null,
      },
      esPropio: true,
      metodoPago: 'EFECTIVO',
      referencia: null,
      total: 300,
      jugadas: [
        { num1: '20', monto: 300 },
      ],
      config,
      esReimpresion: false,
      tipoTicket: 'NUEVO',
      pagaVecesAplicado: '85',
      clienteNombre: 'Cliente',
    };
  }, [bancaNombre, bancaLogo, notasTicketDefault, leyendaTicket, tipoImpresora, telefono]);

  const previewHtml = useMemo(() => renderTicketPreviewHTML(previewPayload), [previewPayload]);

  return (
    <div className="p-4 md:p-6">
      <h1 className="text-2xl font-bold mb-6">Configuración de Ticket</h1>

      <div className="max-w-5xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* ── Formulario ── */}
          <div className="bg-white rounded-lg shadow p-6 space-y-5">
            <h2 className="text-lg font-semibold border-b pb-2">Ajustes del ticket</h2>

            {/* Marca (solo lectura) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Marca <span className="text-gray-400 font-normal">(definida por BANCA)</span>
              </label>
              <div className="w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-md text-gray-600 text-sm">
                {bancaNombre || 'Tiempos V2S General'}
              </div>
            </div>

            {/* Teléfono */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Teléfono del local
              </label>
              <input
                type="tel"
                value={telefono}
                onChange={(e) => setTelefono(e.target.value)}
                placeholder="Ej: 8888-8888"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                maxLength={30}
              />
              <p className="text-xs text-gray-500 mt-1">
                Aparece en el encabezado del ticket impreso
              </p>
            </div>

            {/* Tipo de impresora */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo de impresora
              </label>
              <select
                value={tipoImpresora}
                onChange={(e) => setTipoImpresora(e.target.value === '58mm' ? '58mm' : '80mm')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option value="80mm">80 mm (estándar)</option>
                <option value="58mm">58 mm (compacta)</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Define el ancho y el formato del ticket impreso
              </p>
            </div>

            {/* Leyenda final */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Leyenda final
              </label>
              <input
                type="text"
                value={leyendaTicket}
                onChange={(e) => setLeyendaTicket(e.target.value)}
                placeholder="GRACIAS POR SU PREFERENCIA"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                maxLength={200}
              />
              <p className="text-xs text-gray-500 mt-1">
                Texto que aparece al pie del ticket. Vacío = usa el texto por defecto
              </p>
            </div>

            {/* Notas internas */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notas internas <span className="text-gray-400 font-normal">(opcional)</span>
              </label>
              <textarea
                value={notasTicketDefault}
                onChange={(e) => setNotasTicketDefault(e.target.value)}
                placeholder="Ej: Revise su ticket antes de retirarse"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                rows={2}
                maxLength={500}
              />
            </div>

            {/* Botón guardar */}
            <button
              onClick={guardarConfig}
              disabled={loading}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md font-medium hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Guardando...' : 'Guardar configuración'}
            </button>

            {mensaje && (
              <div className={`p-3 rounded-md text-sm ${
                mensaje.includes('Error') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
              }`}>
                {mensaje}
              </div>
            )}
          </div>

          {/* ── Vista previa del ticket ── */}
          <div className="flex flex-col">
            <h2 className="text-lg font-semibold border-b pb-2 mb-4">Vista previa</h2>
            <div className="flex-1 bg-gray-100 rounded-lg p-4 flex justify-center overflow-auto">
              <div style={{ position: 'relative' }}>
                <iframe
                  title="Vista previa del ticket"
                  srcDoc={previewHtml}
                  className="bg-white shadow-md"
                  style={{
                    width: tipoImpresora === '58mm' ? '220px' : '300px',
                    minHeight: '520px',
                    border: '1px solid #d1d5db',
                    display: 'block',
                  }}
                  sandbox="allow-scripts"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2 text-center">
              Ancho simulado: {tipoImpresora} — los cambios se reflejan en tiempo real
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocalConfigTicketPage;
